import { FormActionTypes } from '@entrata/portals-common/lib/components/forms'
import {
  PAYMENT_SELECTOR_TYPES,
  PAYMENT_TYPES,
  OFAC_PAYMENT_TYPE_IDS,
  LAST_DAY_VALUE,
  CARD_PAYMENT_TYPE_IDS,
  DEFAULT_CA_PAYMENT_TYPE_IDS,
  AUTOPAY_TYPES
} from 'portal-modules/payments/constants/types'
import { APPLE_PAY } from 'portal-modules/payments/constants/keys'
import { errorTypes } from '@entrata/portals-common/lib/components/forms'
import format from 'date-fns/format'
import setDate from 'date-fns/setDate'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import isPast from 'date-fns/isPast'
import { LEASE_STATUS_TYPES } from 'portal-modules/auth/constants/types'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import { display } from 'portal-modules/common/utils/display-show'
import { PLAID_RELINK_BANNER_DAYS } from 'portal-modules/common/constants/plaid'
import { initialFieldObj, isValidPostalCode, isObject } from '@entrata/utils'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'

const { isRP } = getProjectConditions()

export const getInitialPaymentMethodFieldState = (
  {
    method,
    isPaymentAccount = false,
    hideSaveToWallet,
    isAddingMethod = false,
    paymentAccountRequired,
    zipCodeRquired = true,
    requireCvvField = true,
    isFromBulkPayment = false,
    countryCode = '',
    isCheckout,
    ofacCountryCode,
    isDisplayDefaultAccount,
    isFrom = ''
  } = {},
  prefix = '',
) => {
  let {
    id,
    bank_info = {},
    card_info = {},
    sepa_info = {},
    nickname,
    payment_type_id,
    is_default_account,
    payment_method_screening = {}
  } = method || {}

  let type =
    payment_type_id == 4
      ? PAYMENT_SELECTOR_TYPES.ECHECK
      : payment_type_id == 13
        ? PAYMENT_SELECTOR_TYPES.SEPA
        : payment_type_id == 14
          ? PAYMENT_SELECTOR_TYPES.PAD
          : payment_type_id == 22
            ? PAYMENT_SELECTOR_TYPES.BACS
            : PAYMENT_SELECTOR_TYPES.CREDIT

  let bankNumber = ''
  let bankTransitNumber = ''
  if (payment_type_id == 14 && bank_info && bank_info.routing_number) {
    bankNumber = bank_info.routing_number.substr(1, 3)
    bankTransitNumber = bank_info.routing_number.substr(4, 5)
  }
  // we want to include the method fields in form state if we have an initial payment method (!!id)
  // AND if it isn't a payment account (payment_method !== 'new')
  // OR if we are adding a new method.
  let includeNewMethodFields = (!!id && !isPaymentAccount) || isAddingMethod
  let isCredit = type === PAYMENT_SELECTOR_TYPES.CREDIT
  let isSepa = type === PAYMENT_SELECTOR_TYPES.SEPA
  let isBACS = type === PAYMENT_SELECTOR_TYPES.BACS
  let isRpSepa = getProjectName() == 'rp' && isSepa
  let isPad = type === PAYMENT_SELECTOR_TYPES.PAD

  // We also want to make sure that the cvv field is ignored if we are editting an existing payment method
  let ignoreCVV = !!id

  let month = card_info.exp_month || card_info.exp_date_month
  let year = card_info.exp_year || card_info.exp_date_year
  nickname = nickname || bank_info.nickname || card_info.nickname
  // display(`DEBUG - Postal Code Require ${zipCodeRquired}`)
  let initialFields = {
    [`${prefix}payment_method`]: {
      required: !isCheckout ?? true,
      value: id ? method : null,
      ignore: (isCheckout ? isCheckout : isAddingMethod),
    },
    [`${prefix}paymentType`]: {
      value: type,
      ignore: !includeNewMethodFields,
    },
    [`${prefix}nickname`]: {
      value: nickname,
      ignore: isSepa,
      required: isFromBulkPayment,
    },
    [`${prefix}card`]: {
      value: card_info.masked_card_number
        ? { masked_cc_number: card_info.masked_card_number }
        : null,
      required: true,
      ignore: !includeNewMethodFields || !isCredit || isSepa,
    },
    [`${prefix}name_on_card`]: {
      value: card_info.name_on_card,
      required: true,
      ignore: !includeNewMethodFields || !isCredit || isSepa,
    },
    [`${prefix}expiration`]: {
      value: month
        ? `${month > 9 ? month : '0' + month}/${year.toString().substr(2)}`
        : '',
      required: true,
      ignore: !includeNewMethodFields || !isCredit || isSepa || isBACS,
    },
    [`${prefix}cvv`]: {
      value: '',
      required: requireCvvField,
      ignore: !includeNewMethodFields || !isCredit || ignoreCVV || isSepa,
    },
    [`${prefix}postal_code`]: {
      value: card_info.postal_code,
      required: isRpSepa ? false : zipCodeRquired,
      ignore: !includeNewMethodFields || !isCredit || isSepa,
    },
    [`${prefix}bank_name`]: {
      value: bank_info.bank_name,
      ignore: !includeNewMethodFields || isCredit || isSepa,
    },
    [`${prefix}routing_number`]: {
      value: bank_info.routing_number,
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa || isPad,
      disabled: !!id,
    },
    [`${prefix}bank_number`]: {
      value: bankNumber || '',
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa || !isPad,
    },
    [`${prefix}bank_transit_number`]: {
      value: bankTransitNumber || '',
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa || !isPad,
    },
    [`${prefix}account_number`]: {
      value: bank_info.account_number_masked,
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa,
    },
    [`${prefix}name_on_account`]: {
      value: bank_info.name_on_account,
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa,
    },
    [`${prefix}account_type_id`]: {
      value: bank_info.account_type_id,
      required: true,
      ignore: !includeNewMethodFields || isCredit || isSepa || isBACS,
    },
    [`${prefix}make_default_account`]: {
      value: false,
      ignore: !includeNewMethodFields || isCredit || isSepa,
    },

    [`${prefix}save_to_wallet`]: {
      value: true,
      disabled: paymentAccountRequired,
      ignore:
        isAddingMethod ||
        !includeNewMethodFields ||
        hideSaveToWallet ||
        !isPaymentAccount,
    },

    [`${prefix}debtor_name`]: {
      value: sepa_info.debtor_name,
      required: true,
      ignore: !isSepa,
    },

    [`${prefix}debtor_bank_number`]: {
      value: sepa_info.debtor_bank_number,
      required: true,
      ignore: !isSepa,
    },

    [`${prefix}debtor_city`]: {
      value: sepa_info.debtor_city,
      required: isSepa && !isRpSepa,
      ignore: !isSepa && isRpSepa,
    },

    [`${prefix}debtor_postal_code`]: {
      value: sepa_info.debtor_postal_code,
      required: !isSepa,
      ignore: true,
    },

    [`${prefix}debtor_email`]: {
      value: sepa_info.debtor_email,
      required: true,
      ignore: !isSepa,
    },

    [`${prefix}signature_data`]: {
      value: sepa_info.signature_data,
      required: true,
      ignore: !isSepa,
    },

    [`${prefix}debtor_address_line_1`]: {
      value: sepa_info.debtor_address_line_1,
      required: isSepa && !isRpSepa,
      ignore: !isSepa && isRpSepa,
    },

    [`${prefix}debtor_address_line_2`]: {
      value: sepa_info.debtor_address_line_2,
      required: false,
    },

    [`${prefix}is_next_button_enabled`]: {
      value: true,
      required: false,
    },
    [`${prefix}is_finalize_clicked`]: {
      value: true,
      required: false,
    },
  }

  // For CA country code then adding extra key inside the initial field state
  if(isDisplayDefaultAccount) {
    initialFields = {
      ...initialFields,
      [`${prefix}is_default_account`]: {
        value: [AUTOPAY_TYPES.MONTHLY, AUTOPAY_TYPES.BIMONTHLY, AUTOPAY_TYPES.SPLIT].includes(isFrom) ? false : (is_default_account || false), // For auto payments (monthly, bi-monthly, roommate split), the default account is set to false at initial form state by default because payment method editing options are not available. The edit payment method option is only available in the wallet section.
        required: false,
      },
    };
  }

  const isScreeningEnabled = '1' == localStorage.getItem('is_screening_enabled')

  // Use payment_method_screening.country_code if available, otherwise use ofacCountryCode or countryCode
  const screeningCountryCode = payment_method_screening?.country_code || ofacCountryCode || countryCode

  const OfacFormFields = { ...getOfacCardHolderInfo(isScreeningEnabled, screeningCountryCode) }
  initialFields = { ...initialFields, ...OfacFormFields }

  return initialFields
}

export const getFullNameError = (firstName, lastName) => {
  if (!firstName && !lastName) {
    return __('Please enter first and last name')
  }
  if (!firstName) {
    return __('Please enter first name')
  }
  if (!lastName) {
    return __('Please enter last name')
  }
  return ''
}

export const paymentMethodStateReducer = (
  { state, changes: { name, type, payload } },
  prefix = '',
  is_from_bi_monthly = false,
  residentProfile = {}
) => {
  const isScreeningEnabled = '1' == localStorage.getItem('is_screening_enabled')
  let profile = residentProfile

  if (localStorage.getItem('screening_profile') !== null) {
    profile = JSON.parse(localStorage.getItem('screening_profile'))
  }

  if (
    'payment_method_screening[full_name]' === name &&
    'ON_CHANGE' == type
  ) {
    const fullName = (payload[`payment_method_screening[full_name]`].value || '').replace(/^\s+/, '')
    return {
      ...payload,
      [`payment_method_screening[full_name]`]: {
        ...state[`payment_method_screening[full_name]`],
        value: fullName,
        touched: true,
      }
    }
  }

  if (
      'payment_method_screening[full_name]' === name &&
      'ON_BLUR' == type
    ) {
    const fullName = (payload[`payment_method_screening[full_name]`].value || '')
    const nameParts = fullName.split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''
    const fullNameError = getFullNameError(firstName, lastName)
    
    return {
      ...payload,
      [`payment_method_screening[full_name]`]: {
        ...state[`payment_method_screening[full_name]`],
        value: fullName,
        touched: true,
        error: fullNameError
      },
      [`payment_method_screening[name_first]`]: {
        ...state[`payment_method_screening[name_first]`],
        value: firstName,
        touched: true,
        error: !firstName ? state[`payment_method_screening[name_first]`].error : '' 
      },
      [`payment_method_screening[name_last]`]: {
        ...state[`payment_method_screening[name_last]`],
        value: lastName,
        touched: true,
        error: !lastName ? state[`payment_method_screening[name_last]`].error : '' 
      },
      [`payment_method_screening[name_middle]`]: {
        ...state[`payment_method_screening[name_middle]`],
        value: '',
        touched: true,
        error:''
      }
    }
  }

  if (
    'payment_method_screening[name_middle_optional]' === name &&
    'ON_CHANGE' == type
  ) {
    return {
      ...payload,
      [`payment_method_screening[name_middle]`]: {
        ...state[`payment_method_screening[name_middle]`],
        required:
          state[`payment_method_screening[name_middle_optional]`]?.value == true
            ? true
            : false,
        error:
          state[`payment_method_screening[name_middle_optional]`]?.value ==
          false
            ? ''
            : state[`payment_method_screening[name_middle]`].error,
      },
    }
  }

  if (
    'payment_method_screening[default_card_info]' === name &&
    'ON_CHANGE' == type
  ) {
    // Create base payload with common fields
    const basePayload = {
      ...payload,
      [`payment_method_screening[name_first]`]: {
        ...state[`payment_method_screening[name_first]`],
        value: profile?.first_name || '',
        touched: true,
        error:
          profile?.first_name != ''
            ? ''
            : state[`payment_method_screening[name_first]`].error,
      },
      [`payment_method_screening[name_last]`]: {
        ...state[`payment_method_screening[name_last]`],
        value: profile?.last_name || '',
        touched: true,
        error:
          profile?.last_name != ''
            ? ''
            : state[`payment_method_screening[name_last]`].error,
      },
      [`payment_method_screening[name_middle]`]: {
        ...state[`payment_method_screening[name_middle]`],
        value: profile?.middle_name || '',
        touched: true,
        error: !isRP ? '' : (profile?.middle_name != '' ? '' : state[`payment_method_screening[name_middle]`].error),
      },
      [`payment_method_screening[dob]`]: {
        ...state[`payment_method_screening[dob]`],
        touched: true,
      },
      [`payment_method_screening[postal_addresses][country]`]: {
        ...state[`payment_method_screening[postal_addresses][country]`],
        value: profile?.customer_address?.country_code || state[`payment_method_screening[postal_addresses][country]`].value || 'US',
        touched: true,
        error:
          profile?.customer_address?.country_code !== ''
            ? ''
            : state[`payment_method_screening[postal_addresses][country]`]
                .error,
      },
      [`payment_method_screening[postal_addresses][addressLine1]`]: {
        ...state[`payment_method_screening[postal_addresses][addressLine1]`],
        value: profile?.customer_address?.street_line1 || '',
        touched: true,
        error:
          profile?.customer_address?.street_line1 !== ''
            ? ''
            : state[`payment_method_screening[postal_addresses][addressLine1]`].error,
      },
      [`payment_method_screening[postal_addresses][addressLine2]`]: {
        ...state[`payment_method_screening[postal_addresses][addressLine2]`],
        value: profile?.customer_address?.street_line2 || '',
        touched: true,
      },
      [`payment_method_screening[postal_addresses][locality]`]: {
        ...state[`payment_method_screening[postal_addresses][locality]`],
        value: profile?.customer_address?.city || '',
        touched: true,
        error:
          profile?.customer_address?.city !== ''
            ? ''
            : state[`payment_method_screening[postal_addresses][locality]`].error,
      },
      [`payment_method_screening[postal_addresses][administrativeArea]`]: {
        ...state[
          `payment_method_screening[postal_addresses][administrativeArea]`
        ],
        value: profile?.customer_address?.state_code || '',
        touched: true,
        error:
          profile?.customer_address?.state_code !== ''
            ? ''
            : state[
                `payment_method_screening[postal_addresses][administrativeArea]`
              ].error,
      },
      [`payment_method_screening[postal_addresses][postalCode]`]: {
        ...state[`payment_method_screening[postal_addresses][postalCode]`],
        touched: true,
      },
    }

    // Only add full_name field if !isRP
    if (!isRP) {
      basePayload[`payment_method_screening[full_name]`] = {
        ...state[`payment_method_screening[full_name]`],
        value: `${profile?.first_name} ${profile?.last_name}` || '',
        touched: true,
        error: getFullNameError(profile?.first_name, profile?.last_name),
      }
    }

    return basePayload
  }

  if (
    'debtor_bank_number' === name &&
    ('ON_FOCUS' == type || 'ON_BLUR' == type || 'ON_CHANGE' == type)
  ) {
    return {
      ...payload,
      [`${prefix}signature_data`]: {
        ...state[`${prefix}signature_data`],
        value: '',
        error: __('You must resign before saving'),
      },
    }
  }

  if (type === FormActionTypes.onChange) {
    const methodChanged = name === `${prefix}payment_method`
    const typeChanged = (name === `${prefix}paymentType` || name === `paymentType`)
    const amountChanged =
      name === `${prefix}max_payment_amount` ||
      name === `${prefix}custom_amount` ||
      name === `${prefix}payment_amount`
    const cardNumberChanged = name === `${prefix}card`

    if (amountChanged) {
      // reset the payment method each time a field that controls the amount changes
      return {
        ...payload,
        [`${prefix}payment_method`]: {
          ...state[`${prefix}payment_method`],
          value: null,
          error: errorTypes.required,
        },
      }
    }
    if (cardNumberChanged) {
      return {
        ...payload,
        [`${prefix}cvv`]: {
          ...state[`${prefix}cvv`],
          value: null,
          error: errorTypes.required,
        },
      }
    }

    const sepaChanged =
      name === `${prefix}debtor_bank_number` ||
      name === `${prefix}debtor_name` ||
      name === `${prefix}debtor_email` ||
      name === `${prefix}debtor_address_line_1` ||
      name === `${prefix}debtor_address_line_2` ||
      name === `${prefix}debtor_city` ||
      name === `${prefix}debtor_postal_code`
    if (sepaChanged) {
      return {
        ...payload,
        [`${prefix}signature_data`]: {
          ...state[`${prefix}signature_data`],
          value: '',
          error: __('You must resign before saving'),
        },
      }
    }

    if (methodChanged || typeChanged) {
      // The current state for each of these fields depends on which update this reducer is responding to
      // if the payment_type field was updated, we pull its current value from `payload`
      // otherwise we pull its current value from `state`
      // Same goes for payment_method
      let currentMethod = (methodChanged ? payload : state)[
        `${prefix}payment_method`
      ]

      let currentType = (typeChanged ? payload : state)[`${prefix}paymentType`] || (typeChanged ? payload : state)[`paymentType`] 

      let selectedType = 'credit'
      if (typeChanged && currentType.value == 4) {
        selectedType = 'eCheck'
      }

      if (typeChanged && currentType.value == 13) {
        selectedType = 'sepa'
      }

      if (typeChanged && currentType.value == 14) {
        selectedType = 'pad'
      }
      // The PaymentMethodField sets payment_method to `new` when the user is entering a new method
      // when making or scheduling a payment
      let isNewPaymentMethod = currentMethod?.value === 'new'
      let isSepa = selectedType === PAYMENT_SELECTOR_TYPES.SEPA
      let isRpSepa = getProjectName() == 'rp' && isSepa
      // The payment method fields (echeck/credit) should be ignored if the payment_method
      // field isn't ignored and if its value isn't `new`
      let ignoreMethodFields =
        !currentMethod?.ignore && !isNewPaymentMethod && !isSepa

      let isCredit = selectedType === PAYMENT_SELECTOR_TYPES.CREDIT
      let isPad = selectedType === PAYMENT_SELECTOR_TYPES.PAD

      let ignoreCredit = !isCredit || ignoreMethodFields
      let ignoreBank = isCredit || ignoreMethodFields

      let isCvvRequired =
        payload && payload.paymentType && payload.paymentType.requireCvv

      let responsePayload = {
        ...payload,
        [`${prefix}paymentType`]: {
          ...(typeChanged ? payload : state)[`${prefix}paymentType`],
          ignore: ignoreMethodFields,
        },
        [`${prefix}nickname`]: {
          ...state[`${prefix}nickname`],
          required: !isSepa,
          ignore: ignoreMethodFields || isSepa,
          error: isSepa ? '' : errorTypes.required,
        },
        [`${prefix}card`]: {
          ...state[`${prefix}card`],
          ignore: ignoreCredit,
          error: errorTypes.required,
        },
        [`${prefix}card`]: { ...state[`${prefix}card`], ignore: ignoreCredit },
        [`${prefix}name_on_card`]: {
          ...state[`${prefix}name_on_card`],
          ignore: ignoreCredit || isSepa,
        },
        [`${prefix}expiration`]: {
          ...state[`${prefix}expiration`],
          ignore: ignoreCredit || isSepa,
        },
        [`${prefix}cvv`]: {
          ...state[`${prefix}cvv`],
          ignore: !isCvvRequired,
          required: isCvvRequired,
        },
        [`${prefix}postal_code`]: {
          ...state[`${prefix}postal_code`],
          ignore: ignoreCredit || isSepa,
        },
        [`${prefix}routing_number`]: {
          ...state[`${prefix}routing_number`],
          ignore: ignoreBank || isSepa || isPad,
        },
        [`${prefix}bank_number`]: {
          ...state[`${prefix}bank_number`],
          ignore: ignoreBank || isSepa || !isPad,
        },
        [`${prefix}bank_transit_number`]: {
          ...state[`${prefix}bank_transit_number`],
          ignore: ignoreBank || isSepa || !isPad,
        },
        [`${prefix}account_number`]: {
          ...state[`${prefix}account_number`],
          ignore: ignoreBank || isSepa,
          error: errorTypes.required,
        },
        [`${prefix}name_on_account`]: {
          ...state[`${prefix}name_on_account`],
          ignore: ignoreBank || isSepa,
          error: errorTypes.required,
        },
        [`${prefix}account_type_id`]: {
          ...state[`${prefix}account_type_id`],
          ignore: ignoreBank || isSepa,
        },
        [`${prefix}make_default_account`]: {
          ...state[`${prefix}make_default_account`],
          ignore: ignoreBank || isSepa,
        },
        [`${prefix}save_to_wallet`]: {
          ...state[`${prefix}save_to_wallet`],
          ignore: !isNewPaymentMethod,
        },
        [`${prefix}debtor_name`]: {
          ...state[`${prefix}debtor_name`],
          ignore: !isSepa,
        },

        [`${prefix}debtor_bank_number`]: {
          ...state[`${prefix}debtor_bank_number`],
          ignore: !isSepa,
        },

        [`${prefix}debtor_email`]: {
          ...state[`${prefix}debtor_email`],
          ignore: !isSepa,
        },
        [`${prefix}signature_data`]: {
          ...state[`${prefix}signature_data`],
          ignore: !isSepa,
        },
        [`${prefix}terms_and_conditions`]: {
          ...state[`${prefix}terms_and_conditions`],
        },
        [`${prefix}bank_transit_number`]: {
          ...state[`${prefix}bank_transit_number`],
          ignore: ignoreBank || isSepa || !isPad,
        },
        ...getScreeningFields(state, isScreeningEnabled, selectedType),
        ...['payment_day', 'start_month', 'end_month'].reduce(
          (obj, el) =>
            state[`${el}`] && {
              ...obj,
              [`${el}`]: {
                ...state[`${el}`],
                error:
                  state[`${el}`].value != undefined ||
                  state[`${el}`].value != null
                    ? ''
                    : state[`${el}`]
                    ? !is_from_bi_monthly && errorTypes.required
                    : '',
              },
            },
          {},
        ),
      }
      return responsePayload
    }

    if (name === `${prefix}save_to_wallet`) {
      // Nickname should be required for all methods that are saved to the wallet
      return {
        ...payload,
        [`${prefix}nickname`]: {
          ...state[`${prefix}nickname`],
          required: payload[`${prefix}save_to_wallet`].value,
        },
      }
    }
  }
}

const getOfacPayload = (payload, values) => {
  const basePayload = {
    ...payload,
    [`payment_method_screening[postal_addresses][country]`]:
      values[`payment_method_screening[postal_addresses][country]`],
    [`payment_method_screening[postal_addresses][addressLine1]`]:
      values[`payment_method_screening[postal_addresses][addressLine1]`],
    [`payment_method_screening[postal_addresses][addressLine2]`]:
      values[`payment_method_screening[postal_addresses][addressLine2]`],
    [`payment_method_screening[postal_addresses][locality]`]:
      values[`payment_method_screening[postal_addresses][locality]`],
    [`payment_method_screening[postal_addresses][administrativeArea]`]:
      values[
        `payment_method_screening[postal_addresses][administrativeArea]`
      ],
    [`payment_method_screening[postal_addresses][postalCode]`]:
      values[`payment_method_screening[postal_addresses][postalCode]`],
    [`payment_method_screening[name_first]`]:
      values[`payment_method_screening[name_first]`],
    [`payment_method_screening[name_middle]`]:
      values[`payment_method_screening[name_middle]`],
    [`payment_method_screening[name_last]`]:
      values[`payment_method_screening[name_last]`],
    [`payment_method_screening[dob]`]:
      values[`payment_method_screening[dob]`],
    [`payment_method_screening[is_self_verified]`]:
      values[`payment_method_screening[is_self_verified]`]
  }

  if (!isRP) {
    const firstName = values[`payment_method_screening[name_first]`] || ''
    const lastName = values[`payment_method_screening[name_last]`] || ''
    basePayload[`payment_method_screening[full_name]`] = `${firstName} ${lastName}`.trim()
  }

  return basePayload
}

export const getPaymentMethodPayload = (values = {}, prefix = '') => {
  let nickname = values[`${prefix}nickname`]
  let payload = {
    save_to_wallet:
      values.hasOwnProperty(`${prefix}save_to_wallet`) &&
      values[`${prefix}save_to_wallet`],
    nickname,
  }

  if (
     OFAC_PAYMENT_TYPE_IDS.includes(values[`${prefix}paymentType`]) ||
     OFAC_PAYMENT_TYPE_IDS.includes(values[`paymentType`]) ||
     OFAC_PAYMENT_TYPE_IDS.includes(values[`payment_type_id`])
  ) {
    if ('1' == localStorage.getItem('is_screening_enabled')) {
      payload = getOfacPayload(payload,values);
    }

    if(
      CARD_PAYMENT_TYPE_IDS.includes(values[`${prefix}paymentType`]) ||
      CARD_PAYMENT_TYPE_IDS.includes(values[`paymentType`]) ||
      CARD_PAYMENT_TYPE_IDS.includes(values[`payment_type_id`])
    ){
      const [month, year] = values[`${prefix}expiration`].split('/')
      const { payment_type_id, masked_cc_num, token, ...rest } =
        values[`${prefix}card`]
  
      let yearPrefix = new Date().getFullYear().toString().substr(0, 2)
  
      payload = {
        ...payload,
        payment_type_id,
        card_info: {
          ...rest,
          nickname,
          cvv: values[`${prefix}cvv`],
          name_on_card: values[`${prefix}name_on_card`],
          exp_month: month.trim(),
          exp_year: `${yearPrefix}${year.trim()}`,
          postal_code:values[`${prefix}postal_code`],
          masked_card_number: masked_cc_num,
          secure_token: token,
        },
      }
    }
    else {
    payload = {
      ...payload,
      payment_type_id: values[`${prefix}paymentType`],
      bank_info: {
        name_on_account: values[`${prefix}name_on_account`],
        bank_name: values[`${prefix}bank_name`],
        routing_number: values[`${prefix}routing_number`],
        account_number: values[`${prefix}account_number`],
        account_type_id: values[`${prefix}account_type_id`],
        bank_number: values[`${prefix}bank_number`],
        bank_transit_number: values[`${prefix}bank_transit_number`],
        nickname,
      },
    }
  }
  } else if (values[`${prefix}paymentType`] == 20) {
    let [month, year] = values[`${prefix}expiration`].split('/')
    let { payment_type_id, masked_cc_num, ...rest } =
      values[`${prefix}card`]
    let yearPrefix = new Date().getFullYear().toString().substr(0, 2)
    // card_info
    payload = {
      ...payload,
      payment_type_id: payment_type_id,
      card_info: {
        ...rest,
        nickname,
        cvv: values[`${prefix}cvv`],
        name_on_card: values[`${prefix}name_on_card`],
        exp_month: month.trim(),
        exp_year: `${yearPrefix}${year.trim()}`,
        postal_code: values[`${prefix}postal_code`],
        masked_card_number: masked_cc_num,
      },
    }
  } else if (values[`${prefix}paymentType`] == 13) {
    const { token, ...rest } = values[`${prefix}debtor_bank_number`]

    payload = {
      ...payload,
      payment_type_id: PAYMENT_TYPES.SEPA.value,
      sepa_info: {
        debtor_city: values[`${prefix}debtor_city`],
        debtor_postal_code: values[`${prefix}debtor_postal_code`],
        debtor_email: values[`${prefix}debtor_email`],
        debtor_name: values[`${prefix}debtor_name`],
        signature_data: values[`${prefix}signature_data`],
        signed_on: i18nUtils.datetime(new Date().toISOString(), 'DATETIME_ISO'),
        creditor_id: values.creditor_id,
        creditor_name: values.company_name,
        creditor_address: values.company_address,
        debtor_address_line_1: values[`${prefix}debtor_address_line_1`],
        debtor_address_line_2: values[`${prefix}debtor_address_line_2`],
        nickname,
        iban_token: token || '',
        ...rest,
      },
    }
  }
  return payload
}

export const getInitialFieldState = ({
  amount,
  balance,
  is_duplicate_payment,
  isNextButtonEnabled = false,
  ...rest
}) => {
  let initialState = {
    agrees_to_terms: { required: !isNextButtonEnabled, value: false },
    agrees_to_duplicate: {
      required: is_duplicate_payment,
    },
    custom_amount: {
      required: true,
      ignore: true,
      value: amount && balance != amount ? amount : '',
    },
    payment_amount: {
      required: true,
      value: !amount || amount == balance ? 'total_balance' : 'other_amount',
    },
    payment_memo: {},
    promise_to_pay: { value: false, required: false },
    ...getInitialPaymentMethodFieldState(rest),
  }
  return initialState
}

export const fieldStateReducer = data => {
  const {
    state,
    changes: { name, type, payload },
  } = data

  if (type === FormActionTypes.onChange) {
    if (name === 'payment_amount') {
      return {
        ...payload,
        ...(state.max_payment_amount && {
          max_payment_amount: {
            ...state.max_payment_amount,
            ignore: !state.max_payment_amount.ignore,
          },
        }),
        custom_amount: {
          ...state.custom_amount,
          ignore: !state.custom_amount.ignore,
        },
        payment_method: {
          ...state.payment_method,
          value: '',
          error: errorTypes.required,
        },
      }
    } else if (name === 'custom_amount') {
      return {
        ...payload,
        payment_method: {
          ...state.payment_method,
          value: '',
          error: errorTypes.required,
        },
      }
    } else if (name === 'payment_day') {
      let now = new Date()
      if (
        state.start_month.value &&
        new Date(state.start_month.value).getMonth() == now.getMonth() &&
        payload.payment_day.value < new Date().getDate()
      ) {
        return {
          ...payload,
          start_month: {
            ...state.start_month,
            value: '',
            error: errorTypes.required,
          },
        }
      }
      else{
        return {
          ...payload,
          payment_day: {
            ...payload?.payment_day,
            value: payload?.payment_day?.value ? payload?.payment_day?.value : null,
            error: payload?.payment_day?.value ? '' : __('Required field'),
          },
        }
      }
    } else if (name === 'agrees_to_duplicate') {
      return {
        ...payload,
        agrees_to_duplicate: {
          ...payload.agrees_to_duplicate,
          required: true,
        },
      }
    } else if (name === 'promise_to_pay') {
      return {
        ...payload,
        promise_to_pay: {
          ...payload.promise_to_pay,
          required: true,
        },
      }
    } else if (name === 'end_month') {
      return {
        ...payload,
        end_month: {
          ...payload.end_month,
          value: payload.end_month.value ? payload.end_month.value : null,
        },
      }
    } else if (name === 'start_month') {
      return {
        ...payload,
        start_month: {
          ...payload.start_month,
          value: payload?.start_month?.value || null,
        },
      }
    }
  }

  if (name === 'postal_code' && getProjectName() != 'tp') {
    return {
      ...payload,
      postal_code: {
        ...state.postal_code,
        error:
          !!state.postal_code.value && state.postal_code.required
            ? __('Required Field')
            : '',
      },
    }
  }

  const paymentPayload = paymentMethodStateReducer(data)
  return paymentPayload || payload
}

export const getStartDate = ({ month, day }) => {
  const formattedDate = new Date(
    format(new Date(month), 'yyyy'),
    format(new Date(month), 'MM') - 1,
  )
  const paymentDay =
    day == 29 || day == 30 || day == 31
      ? +getDaysInMonth(formattedDate) - LAST_DAY_VALUE[day]
      : +day
  const startDate = format(
    setDate(new Date(+month), paymentDay),
    "yyyy-MM-dd'T'HH:mm:ss",
  )

  return startDate
}

export const getConvenienceFee = ({
  method,
  waiveCreditFee,
  waiveDebitFee,
  leaseStatus,
  currencyCode,
}) => {
  let convenienceFee = method?.fee || 0
  const isCCCard = method && [5, 6, 7, 8].includes(+method.payment_type_id)
  let isInternationalCard = false
  let isCommertialCard = false
  let isDebitCard = false

  if (isCCCard && method) {
    isInternationalCard = method.card_info.is_international
    isCommertialCard = method.card_info.is_commercial
    isDebitCard = method.card_info.is_debit_card

    if (
      'CAD' === currencyCode &&
      (true == method.card_info.is_international ||
        true == method.card_info.is_prepaid_card)
    ) {
      isDebitCard = false
    }
  }

  if (method && isDebitCard) {
    convenienceFee = method.debit_fee
  }
  if (isInternationalCard && method) {
    convenienceFee = isDebitCard
      ? method.international_debit_fee > 0
        ? method.international_debit_fee
        : convenienceFee
      : method.international_fee > 0
      ? method.international_fee
      : convenienceFee
  } else if (isCommertialCard && !isInternationalCard && method) {
    convenienceFee = isDebitCard
      ? method.commercial_debit_fee > 0
        ? method.commercial_debit_fee
        : convenienceFee
      : method.commercial_fee > 0
      ? method.commercial_fee
      : convenienceFee
  }
  if (convenienceFee > 0 && leaseStatus === LEASE_STATUS_TYPES.PAST) {
    if ((isDebitCard && waiveDebitFee) || (!isDebitCard && waiveCreditFee)) {
      convenienceFee = 0
    }
  }
  return convenienceFee
}

export const getHeaderName = ({ id }) => {
  if (!id) return false
  let name = ''
  switch (id) {
    case PAYMENT_TYPES.ECHECK.value:
      name = __('Bank Account ACH')
      break
    case PAYMENT_TYPES.SEPA.value:
      name = __('New SEPA Direct Debit Core Scheme')
      break
    case PAYMENT_TYPES.BACS.value:
        name = __('Banks Direct Debit')
        break  
    case PAYMENT_TYPES.PAD.value:
      name = __('Pre-Authorized Debit')
      break
    default:
      name = __('Debit / Credit Card')
      break
  }
  return name
}

export const getAccountRelinkDetails = ({ paymentMethods }) => {
  let microDepositAccountId = null

  var date = new Date()
  date.setDate(date.getDate() + PLAID_RELINK_BANNER_DAYS)

  const plaidExpiredAccounts = Object.values(paymentMethods).filter(
    method =>
      method.bank_info &&
      method.bank_info.bank_name != 'TD Bank' &&
      method.bank_info.plaid_verification_status != 'verification_expired' &&
      method.bank_info.plaid_consent_expiration_time &&
      (isPast(method.bank_info.plaid_consent_expiration_time) ||
        i18nUtils.datetime(
          method?.bank_info?.plaid_consent_expiration_time,
          'DATETIME_ISO',
        ) < i18nUtils.datetime(date.toISOString(), 'DATETIME_ISO') ||
        method?.bank_info?.plaid_item_error?.error_code ==
          'ITEM_LOGIN_REQUIRED'),
  )

  const microDepositAccounts = Object.values(paymentMethods).filter(
    method =>
      method.bank_info &&
      method.bank_info.plaid_verification_status &&
      method.bank_info.plaid_verification_status ==
        'pending_manual_verification',
  )

  const latestExpiredMethod =
    plaidExpiredAccounts.length &&
    plaidExpiredAccounts.reduce((a, b) => {
      return new Date(a.bank_info.plaid_consent_expiration_time) >
        new Date(b.bank_info.plaid_consent_expiration_time)
        ? a
        : b
    })

  microDepositAccounts.map(account => {
    microDepositAccountId = account.id
  })

  return {
    relinkAccountId:
      (4 == latestExpiredMethod.payment_type_id &&
      'undefined' != typeof latestExpiredMethod.payment_account_id
        ? latestExpiredMethod.payment_account_id
        : latestExpiredMethod.id) || null,
    microDepositAccountId: microDepositAccountId,
    showRelinkBanner: !!plaidExpiredAccounts.length,
    showMicroDepositBanner: !!microDepositAccounts.length,
  }
}

export const filterConvFeeFromAvailable = (
  available,
  { card_info, payment_type_id },
) => {
  const paymentType =
    Object.keys(available).length &&
    [...available].filter(paymentType => paymentType.id == payment_type_id)
  if (!paymentType.length) return 0

  return CARD_PAYMENT_TYPE_IDS.includes(payment_type_id)
    ? card_info.is_debit_card ||
      card_info.is_check_card ||
      card_info.is_gift_card ||
      card_info.is_prepaid_card
      ? paymentType[0].debit_fee
      : paymentType[0].fee
    : paymentType[0].fee || 0
}

export const filterUnavailablePaymentMethods = (methods, availableMethods) => {
  const paymentTypes = availableMethods?.map(({ id }) => id == APPLE_PAY ? PAYMENT_TYPES.APPLE_PAY.value : id)
  const filteredMethods = methods.reduce(function (result, method) {
    if (paymentTypes.includes(method.payment_type_id)) {
      result.push(method)
    }
    return result
  }, [])

  return filteredMethods
}

export const filterPaymentMethodByKey = (paymentMethods, key) => {
  if (!paymentMethods || !key) return false
  return (
    paymentMethods &&
    Object.values(paymentMethods).filter(
      method => method[key] && Object.keys(method[key]).length,
    )
  )
}

export const sortingPaymentMethods = paymentMethods =>
  paymentMethods?.map(({ created_on }, i) => ({
    ...paymentMethods[i],
    created_on: new Date(created_on)?.getTime(),
  }))

export const mapOFACValuesToField = (values, fields) => {
  if (false == isObject(values) || !values['country_code']) return fields
  fields['payment_method_screening[postal_addresses][country]'].value =
    values['country_code']
  fields['payment_method_screening[postal_addresses][addressLine1]'].value =
    values['street_line1']
  fields['payment_method_screening[postal_addresses][addressLine2]'].value =
    values['street_line2']
  fields['payment_method_screening[postal_addresses][locality]'].value =
    values['city']
  fields[
    'payment_method_screening[postal_addresses][administrativeArea]'
  ].value = values['state_code']
  fields['payment_method_screening[postal_addresses][postalCode]'].value =
    values['postal_code']
  fields['payment_method_screening[name_first]'].value = values['name_first']
  fields['payment_method_screening[name_last]'].value = values['name_last']
  fields['payment_method_screening[name_middle]'].value = values['name_middle']
  fields['payment_method_screening[dob]'].value = values['birth_date']
  if (!isRP) {
    fields['payment_method_screening[full_name]'].value = `${values['name_first']} ${values['name_last']}`.trim()
  }
  return fields
}

export const getOfacCardHolderInfo = (isScreeningEnabled, countryCode) => {
  let profile = {}

  if (localStorage.getItem('screening_profile') !== null) {
    profile = JSON.parse(localStorage.getItem('screening_profile'))
  }

  // Create base fields object
  const baseFields = {
    [`payment_method_screening[name_first]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
    },
    [`payment_method_screening[name_middle]`]: {
      value: '',
      required: isRP ? true : false,
      ignore: !isScreeningEnabled,
    },
    [`payment_method_screening[name_last]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
    },
    [`payment_method_screening[dob]`]: {
      value: !isRP ? '01-01-1980' : '',
      required: true,
      ignore: !isScreeningEnabled,
      maxDate: new Date()
    },
    [`payment_method_screening[default_card_info]`]: {
      value: false,
      ignore: !isScreeningEnabled,
    },
    [`payment_method_screening[name_middle_optional]`]: {
      value: false,
      ignore: !isScreeningEnabled,
    },
    [`payment_method_screening[postal_addresses][country]`]: {
      value: countryCode || 'US',
      required: true,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][addressLine1]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][addressLine2]`]: {
      value: '',
      required: false,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][locality]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][administrativeArea]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][postalCode]`]: {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][dependentLocality]`]: {
      value:'',
      required: false,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[postal_addresses][sortingCode]`]: {
      value:'',
      required: false,
      ignore: !isScreeningEnabled,
      title:''
    },
    [`payment_method_screening[is_self_verified]`]: {
      value: true,
      required: true,
      ignore: !isScreeningEnabled,
    }
  }

  // Conditionally add full_name field only when !isRP
  if (!isRP) {
    baseFields[`payment_method_screening[full_name]`] = {
      value: '',
      required: true,
      ignore: !isScreeningEnabled,
    }
  }

  return baseFields
}

export const getBareScreeningFields = () => {
  const baseFields = [
    'payment_method_screening[postal_addresses][country]',
    'payment_method_screening[postal_addresses][addressLine1]',
    'payment_method_screening[postal_addresses][addressLine2]',
    'payment_method_screening[postal_addresses][locality]',
    'payment_method_screening[postal_addresses][administrativeArea]',
    'payment_method_screening[postal_addresses][postalCode]',
    'payment_method_screening[name_first]',
    'payment_method_screening[name_last]',
    'payment_method_screening[name_middle]',
    'payment_method_screening[dob]',
    'payment_method_screening[default_card_info]',
    'payment_method_screening[name_middle_optional]',
    'payment_method_screening[is_self_verified]'
  ]

  if (!isRP) {
    baseFields.push('payment_method_screening[full_name]')
  }

  return baseFields
}

const getScreeningFields = (state, isScreeningEnabled, selectedType) => {
  let newFields = []

  let isScreeningEnabledNew =
    '1' == localStorage.getItem('is_screening_enabled')

  const screeningFields = getBareScreeningFields()
  for (let fieldName in screeningFields) {
    newFields[screeningFields[fieldName]] = {
      ...state[screeningFields[fieldName]],
      ignore: !isScreeningEnabledNew,
      error: isScreeningEnabledNew
          ? state[screeningFields[fieldName]].error
          : '',
    }
  }

  return newFields
}

export const getScreeningFormFields = fields => {
  let newFields = {}

  const screeningFields = getBareScreeningFields()
  for (let fieldName in screeningFields) {
    newFields[`${screeningFields[fieldName]}`] =
      fields[screeningFields[fieldName]]
  }

  return newFields
}

export const getOFACFields = countryCode => {
  const baseFields = [
    'payment_method_screening[postal_addresses][addressLine1]',
    'payment_method_screening[postal_addresses][country]',
    'payment_method_screening[postal_addresses][addressLine2]',
    'payment_method_screening[postal_addresses][locality]',
    'payment_method_screening[postal_addresses][administrativeArea]',
    'payment_method_screening[postal_addresses][postalCode]',
    'payment_method_screening[name_first]',
    'payment_method_screening[name_last]',
    'payment_method_screening[name_middle]',
    'payment_method_screening[dob]',
    'payment_method_screening[is_self_verified]'
  ]

  if (!isRP) {
    baseFields.push('payment_method_screening[full_name]')
  }

  return baseFields
}

export const getOFACFormFields = (countryCode, fields) => {
  const fieldScreeningPostalAddresses = initialFieldObj(
    'payment_method_screening[postal_addresses]',
    countryCode,
    true,
  )

  const baseFields = {
    'payment_method_screening[postal_addresses][country]':
      fields['payment_method_screening[postal_addresses][country]'],
    'payment_method_screening[postal_addresses][addressLine1]':
      fields['payment_method_screening[postal_addresses][addressLine1]'],
    'payment_method_screening[postal_addresses][addressLine2]':
      fields['payment_method_screening[postal_addresses][addressLine2]'],
    'payment_method_screening[postal_addresses][locality]':
      fields['payment_method_screening[postal_addresses][locality]'],
    'payment_method_screening[postal_addresses][administrativeArea]':
      fields['payment_method_screening[postal_addresses][administrativeArea]'],
    'payment_method_screening[postal_addresses][postalCode]':
      fields['payment_method_screening[postal_addresses][postalCode]'],
    'payment_method_screening[name_first]':
      fields['payment_method_screening[name_first]'],
    'payment_method_screening[name_last]':
      fields['payment_method_screening[name_last]'],
    'payment_method_screening[name_middle]':
      fields['payment_method_screening[name_middle]'],
    'payment_method_screening[dob]': fields['payment_method_screening[dob]'],
    'payment_method_screening[is_self_verified]': fields['payment_method_screening[is_self_verified]']
  }

  if (!isRP) {
    baseFields['payment_method_screening[full_name]'] = fields['payment_method_screening[full_name]']
  }

  return baseFields
}

export const getOFACAllFields = (countryCode, fields) => {
  const baseFields = [
    {
      field: fields['payment_method_screening[postal_addresses][country]'],
      name: 'payment_method_screening[postal_addresses][country]',
    },
    {
      field: fields['payment_method_screening[postal_addresses][addressLine1]'],
      name: 'payment_method_screening[postal_addresses][addressLine1]',
    },
    {
      field: fields['payment_method_screening[postal_addresses][addressLine2]'],
      name: 'payment_method_screening[postal_addresses][addressLine2]',
    },
    {
      field: fields['payment_method_screening[postal_addresses][locality]'],
      name: 'payment_method_screening[postal_addresses][locality]',
    },
    {
      field:
        fields[
          'payment_method_screening[postal_addresses][administrativeArea]'
        ],
      name: 'payment_method_screening[postal_addresses][administrativeArea]',
    },
    {
      field: fields['payment_method_screening[postal_addresses][postalCode]'],
      name: 'payment_method_screening[postal_addresses][postalCode]',
    },
    {
      field: fields['payment_method_screening[name_first]'],
      name: 'payment_method_screening[name_first]',
    },
    {
      field: fields['payment_method_screening[name_last]'],
      name: 'payment_method_screening[name_last]',
    },
    {
      field: fields['payment_method_screening[name_middle]'],
      name: 'payment_method_screening[name_middle]',
    },
    {
      field: fields['payment_method_screening[dob]'],
      name: 'payment_method_screening[dob]',
    },
    {
      field: fields['payment_method_screening[is_self_verified]'],
      name: 'payment_method_screening[is_self_verified]',
    }
  ]

  if (!isRP) {
    baseFields.push({
      field: fields['payment_method_screening[full_name]'],
      name: 'payment_method_screening[full_name]',
    })
  }

  return baseFields
}

export const getOFACValues = (countryCode, fields) => {
  const baseValues = {
    'payment_method_screening[postal_addresses][country]':
      fields['payment_method_screening[postal_addresses][country]']?.value,
    'payment_method_screening[postal_addresses][addressLine1]':
      fields['payment_method_screening[postal_addresses][addressLine1]']?.value,
    'payment_method_screening[postal_addresses][addressLine2]':
      fields['payment_method_screening[postal_addresses][addressLine2]']?.value,
    'payment_method_screening[postal_addresses][locality]':
      fields['payment_method_screening[postal_addresses][locality]']?.value,
    'payment_method_screening[postal_addresses][administrativeArea]':
      fields['payment_method_screening[postal_addresses][administrativeArea]']
        ?.value,
    'payment_method_screening[postal_addresses][postalCode]':
      fields['payment_method_screening[postal_addresses][postalCode]']?.value,
    'payment_method_screening[name_first]':
      fields['payment_method_screening[name_first]']?.value,
    'payment_method_screening[name_last]':
      fields['payment_method_screening[name_last]']?.value,
    'payment_method_screening[name_middle]':
      fields['payment_method_screening[name_middle]']?.value,
    'payment_method_screening[dob]':
      fields['payment_method_screening[dob]']?.value,
    'payment_method_screening[is_self_verified]':
      fields['payment_method_screening[is_self_verified]']?.value
  }

  if (!isRP) {
    const firstName = fields['payment_method_screening[name_first]']?.value || ''
    const lastName = fields['payment_method_screening[name_last]']?.value || ''
    baseValues['payment_method_screening[full_name]'] = `${firstName} ${lastName}`.trim()
  }

  return baseValues
}

export const paymentScreeningFieldStateReducer = (name, payload, state) => {
  const fieldNames = [
    'addressLine1',
    'addressLine2',
    'locality',
    'administrativeArea',
    'postalCode',
    'sortingCode',
    'dependentLocality'
  ];
  
  let fieldScreeningPostalAddresses = initialFieldObj(
    'payment_method_screening[postal_addresses]',
    payload[name].value,
    true,
  );

  const updatedFieldScreeningPostalAddresses = { ...fieldScreeningPostalAddresses };

  for (const field of fieldNames) {
    const fieldName = `payment_method_screening[postal_addresses][${field}]`;
    // Update the specific field in the new object
    updatedFieldScreeningPostalAddresses[fieldName] = {
      ...state[fieldName],
      value: '',
      error: fieldScreeningPostalAddresses[fieldName]?.required ? 'REQUIRED' : '',
      required: fieldScreeningPostalAddresses[fieldName]?.required ? true : false,
    };
  }

 
  updatedFieldScreeningPostalAddresses[name] = {
    ...payload[name],
    value: payload[name].value,
  };
  
  return {
    ...state,
    ...payload,
    ...updatedFieldScreeningPostalAddresses,
    
  };
};
  
export const getOfacScreeningProfile = () => {

  let profile = {}

    if (localStorage.getItem('screening_profile') !== null) {
      profile = JSON.parse(localStorage.getItem('screening_profile'))
    }

  return profile
}

/**
 * Retrieves the country code from the client's property address if available.
 * Falls back to the client's general country code if the property address country code is 
 * null, undefined, or an empty string.
 * 
 * @param {Object} client - The client object containing address and country code information.
 * @param {string} client.country_code - The client's default country code.
 * @param {Object} client.property_address - The client's property address object.
 * @param {string|null} client.property_address.country_code - The country code associated with the property address.
 * 
 * @returns {string} - Returns the country code from the client's property address, or falls back to the client's general country code.
 * 
 * @example
 /**
 * Example usage of getClientPropertyCountryCode function.
 *
 * // Case 1: When the property address has a valid country code
 * // If the property address has a valid and non-empty country code, 
 * // the function returns the property address's country code.
 * const client1 = {
 *   country_code: "US", // General country code
 *   property_address: { country_code: "CA" } // Valid property address country code
 * };
 * console.log(getClientPropertyCountryCode(client1)); // Output: "CA"
 * 
 * const client2 = {
 *   country_code: "US", // General country code
 *   property_address: { country_code: "US" } // Valid property address country code
 * };
 * console.log(getClientPropertyCountryCode(client2)); // Output: "US"
 * 
 * // Case 2: When the property address country code is null, undefined, or an empty string
 * // If the property address country code is null, undefined, or an empty string,
 * // the function falls back to the client's general country code.
 *
 * // Example 1: Property country code is null
 * const client3 = {
 *   country_code: "US", // General country code
 *   property_address: { country_code: null } // Null property address country code
 * };
 * console.log(getClientPropertyCountryCode(client3)); // Output: "US"
 * 
 * // Example 2: Property country code is undefined
 * const client4 = {
 *   country_code: "US", // General country code
 *   property_address: { country_code: undefined } // Undefined property address country code
 * };
 * console.log(getClientPropertyCountryCode(client4)); // Output: "US"
 * 
 * // Example 3: Property country code is an empty string
 * const client5 = {
 *   country_code: "US", // General country code
 *   property_address: { country_code: "" } // Empty string as property country code
 * };
 * console.log(getClientPropertyCountryCode(client5)); // Output: "US"
 * 
 * // Case 3: When the property_address object itself is missing or undefined
 * // If the property_address object is completely undefined or missing,
 * // the function safely falls back to the client's general country code.
 * const client6 = {
 *   country_code: "US", // General country code
 *   property_address: undefined // No property_address object
 * };
 * console.log(getClientPropertyCountryCode(client6)); // Output: "US"
 */

// This Function return property_country for RP & RH
export const getClientPropertyCountryCode = (client) => {
  return client?.property_address?.country_code || client?.country_code;
};
// This Function return property_country for TP
export const getClientPropertyCountryCodeForTP = (client) => {
  return client?.address?.country_code || client?.country_code;
};

/**
 * Utility function to determine whether the `is_default_account` field should be included in the payload.
 *
 * This function applies only to Canadian (CA) properties and ensures that the payment type is among the allowed list (`DEFAULT_CA_PAYMENT_TYPE_IDS`).
 * If the conditions are met, it returns an object containing `is_default_account`; otherwise, it returns `null`.
 *
 * @param {string} isDisplayDefaultAccount - Feature Flag.
 * @param {object} values - The values object containing payment type or card details.
 * @param {object} [method] - The selected payment method object (optional).
 * @returns {object|null} - Returns an object with `is_default_account` if conditions are met, otherwise `null`.
 */
export const getIsDefaultPaymentMethodPayloadKey = (isDisplayDefaultAccount, values, method) => {
  if (!isDisplayDefaultAccount) return null;

  // Determine the payment type ID from `method.payment_type_id` if available; otherwise, fallback to `values.card.card_type_id` or `values.paymentType`.
  const paymentTypeId = method?.payment_type_id ?? (values.card ? values.card.card_type_id : values.paymentType);

  // Validate that the payment type is in the allowed list; if not, return null.
  if (!DEFAULT_CA_PAYMENT_TYPE_IDS.includes(paymentTypeId)) return null;

  // Return an object with `is_default_account`,
  return { is_default_account: values.is_default_account };
};
